import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  
  Loader2, 
  Download, 
  Sparkles, 
  TrendingUp,
  Brain,
  ChevronLeft,
  ChevronRight,
  Lock,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { getMonthlyAnalytics, generateMonthlySummary, downloadPdfReport, getMe } from '../lib/api';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

const MOOD_COLORS = {
  peaceful: { bg: 'bg-blue-500/20', text: 'text-blue-400', bar: 'bg-blue-400' },
  anxious: { bg: 'bg-amber-500/20', text: 'text-amber-400', bar: 'bg-amber-400' },
  confused: { bg: 'bg-violet-500/20', text: 'text-violet-400', bar: 'bg-violet-400' },
  frightened: { bg: 'bg-red-500/20', text: 'text-red-400', bar: 'bg-red-400' },
  excited: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', bar: 'bg-emerald-400' },
  sad: { bg: 'bg-gray-500/20', text: 'text-gray-400', bar: 'bg-gray-400' },
  relief: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', bar: 'bg-cyan-400' },
  neutral: { bg: 'bg-gray-500/20', text: 'text-gray-400', bar: 'bg-gray-400' },
};

export default function Analytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  
  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);
  
  const fetchData = async () => {
    setIsLoading(true);
    setAiSummary(null);
    try {
      const [analyticsRes, meRes] = await Promise.all([
        getMonthlyAnalytics(selectedMonth, selectedYear),
        getMe()
      ]);
      setAnalytics(analyticsRes.data);
      setSubscription(meRes.data.subscription);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    try {
      const response = await generateMonthlySummary(selectedMonth, selectedYear);
      setAiSummary(response.data.summary);
      toast.success('Summary generated!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Monthly summaries require ONIRVA Deep subscription');
      } else {
        toast.error('Failed to generate summary');
      }
    } finally {
      setIsGeneratingSummary(false);
    }
  };
  
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const response = await downloadPdfReport(selectedMonth, selectedYear);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ONIRVA_Report_${analytics?.month_name || 'Report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('PDF reports require ONIRVA Deep subscription');
      } else {
        toast.error('Failed to download report');
      }
    } finally {
      setIsDownloading(false);
    }
  };
  
  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    // Don't allow future months
    const targetDate = new Date(newYear, newMonth - 1);
    if (targetDate > now) return;
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };
  
  const canAccessDeepFeatures = subscription?.tier === 'deep' || subscription?.status === 'trial';
  const totalMoodCount = analytics?.mood_distribution 
    ? Object.values(analytics.mood_distribution).reduce((a, b) => a + b, 0) 
    : 0;
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="analytics-page">
      <div className="hero-gradient absolute inset-0" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="px-6 flex items-center justify-between">
          <div>
            {/* Empty space for alignment */}
          </div>
          
          {canAccessDeepFeatures && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={isDownloading || !analytics?.total_dreams}
              className="rounded-full"
              data-testid="download-pdf-btn"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </>
              )}
            </Button>
          )}
        </header>
        
        <div className="px-6">
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-serif font-bold mb-1">Dream Analytics</h1>
            <p className="text-muted-foreground text-sm">Patterns and insights from your dreams</p>
          </div>
          
          {/* Month Selector */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateMonth(-1)}
              className="rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="text-center min-w-[160px]">
              <h2 className="text-xl font-serif font-bold">{analytics?.month_name}</h2>
            </div>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateMonth(1)}
              disabled={selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear()}
              className="rounded-full"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="glass-card p-4 text-center">
              <p className="text-3xl font-serif font-bold text-primary">{analytics?.total_dreams || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Dreams</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-3xl font-serif font-bold text-primary">{analytics?.analyzed_dreams || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Analyzed</p>
            </div>
          </div>
          
          {analytics?.total_dreams === 0 ? (
            <div className="glass-card p-8 text-center">
              <img src={ONIRVA_ICON} alt="" className="w-16 h-16 mx-auto mb-4 opacity-50 rounded-xl" />
              <h3 className="font-serif font-bold mb-2">No dreams this month</h3>
              <p className="text-sm text-muted-foreground">
                Record dreams to see your patterns and insights
              </p>
            </div>
          ) : (
            <>
              {/* Mood Distribution */}
              {analytics?.mood_distribution && Object.keys(analytics.mood_distribution).length > 0 && (
                <div className="glass-card p-5 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <h3 className="font-serif font-bold">Emotional Landscape</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {Object.entries(analytics.mood_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .map(([mood, count]) => {
                        const percentage = Math.round((count / totalMoodCount) * 100);
                        const colors = MOOD_COLORS[mood] || MOOD_COLORS.neutral;
                        
                        return (
                          <div key={mood}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className={`capitalize ${colors.text}`}>{mood}</span>
                              <span className="text-muted-foreground">{count} ({percentage}%)</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${colors.bar} transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              
              {/* Recurring Symbols */}
              {analytics?.symbols?.length > 0 && (
                <div className="glass-card p-5 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <h3 className="font-serif font-bold">Recurring Symbols</h3>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {analytics.symbols.map((item, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1.5 rounded-full bg-primary/20 text-primary text-sm"
                      >
                        {item.symbol} <span className="opacity-60">({item.count})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* AI Summary Section */}
              <div className="glass-card p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" />
                    <h3 className="font-serif font-bold">Monthly Reflection</h3>
                  </div>
                  {!canAccessDeepFeatures && (
                    <span className="flex items-center gap-1 text-xs text-amber-400">
                      <Lock className="w-3 h-3" />
                      Deep
                    </span>
                  )}
                </div>
                
                {aiSummary ? (
                  <div className="prose prose-invert max-w-none">
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap text-sm">
                      {aiSummary}
                    </p>
                  </div>
                ) : canAccessDeepFeatures ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      Generate an AI-powered summary of your month's dreams
                    </p>
                    <Button
                      onClick={handleGenerateSummary}
                      disabled={isGeneratingSummary}
                      className="rounded-full"
                      data-testid="generate-summary-btn"
                    >
                      {isGeneratingSummary ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Summary
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      Monthly reflections available with ONIRVA Deep
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/subscription')}
                      className="rounded-full"
                    >
                      Learn More
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Dreams List */}
              {analytics?.dreams?.length > 0 && (
                <div className="glass-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-primary" />
                    <h3 className="font-serif font-bold">Dreams This Month</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {analytics.dreams.map((dream) => {
                      const date = new Date(dream.date);
                      const moodColors = MOOD_COLORS[dream.mood] || MOOD_COLORS.neutral;
                      
                      return (
                        <div 
                          key={dream.dream_id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dream/${dream.dream_id}`)}
                        >
                          <div className="text-center min-w-[40px]">
                            <p className="text-lg font-bold">{date.getDate()}</p>
                            <p className="text-xs text-muted-foreground">{date.toLocaleDateString('en', { weekday: 'short' })}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{dream.title}</p>
                            <div className="flex items-center gap-2">
                              {dream.mood && (
                                <span className={`text-xs capitalize ${moodColors.text}`}>
                                  {dream.mood}
                                </span>
                              )}
                              {dream.has_analysis && (
                                <span className="text-xs text-primary flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" />
                                  Analyzed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Deep Features Promo */}
          {!canAccessDeepFeatures && analytics?.total_dreams > 0 && (
            <div className="mt-6 glass-card p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold mb-1">Deepen long-term insight</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Monthly reflections, PDF reports, and pattern tracking over time.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => navigate('/subscription')}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
